import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "react-data-table-component";
import { Slide, Fade } from "@mui/material";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RiDeleteBin5Line, RiStopCircleLine } from "react-icons/ri";
import { MdOutlineTerminal, MdOutlineInfo, MdCheckCircle, MdError, MdPending, MdCancel, MdAutorenew } from "react-icons/md";

import ListHeading from "../../../common/RDTListHeading/ListHeading";
import BreadcrumbHeading from "../../../common/RDTListHeading/BreadcrumbHeading";
import ScanLogsPanel from "./ScanLogsPanel";
import SubHeaderComponent from "../../../common/RDTSubHeaderComponent/SubHeaderComponent";
import CustomModal from "../../../common/CustomModal/CustomModal";
import ShowAlert from "../../../common/ShowAlert/ShowAlert";
import DataTableSkeleton from "../../../common/Skeletons/DataTableSkeleton";
import Images from "../../../../utils/images";
import useWindowDimensions from "../../../../utils/getHeightWidth";
import { GetColmn } from "../../../common/CustomColumn/customize_column";
import { changeTitle } from "../../../../state/slices/header";
import GetCapabilities from "../../../../utils/getCapabilities";
import {
  delayTime,
  fadedelayTime,
  slideDirection,
} from "../../../../utils/transitionEffectParams";
import { showError, showSuccess } from "../../../../utils/showMessage";
import { getScanHistory, deleteScan, stopScan } from "../../../../api/credentialScanning";
import FirstPageIcon from "../../../../images/first_page.svg";
import LastPageIcon from "../../../../images/last_page.svg";
import NextPageIcon from "../../../../images/arrow_right.svg";
import PreviousPageIcon from "../../../../images/arrow_left.svg";

import "../index.scss";

const pages_url = "dashboard/discovery/hardcoded-credentials/scan-history";

const STATUS_CONFIG = {
  PENDING:     { bg: "#e3f2fd", color: "#1565c0", Icon: MdPending,     pulse: false },
  IN_PROGRESS: { bg: "#fff8e1", color: "#f57f17", Icon: MdAutorenew,   pulse: true  },
  COMPLETED:   { bg: "#e8f5e9", color: "#2e7d32", Icon: MdCheckCircle, pulse: false },
  FAILED:      { bg: "#ffebee", color: "#c62828", Icon: MdError,       pulse: false },
  CANCELLED:   { bg: "#f5f5f5", color: "#757575", Icon: MdCancel,      pulse: false },
};

// Filter labels are wrapped in t() inside the component (see useMemo below).
// Values stay as wire-stable identifiers.
const FILTER_KEYS = [
  { labelKey: "credentialScanning.scanStatusCompleted",  value: "COMPLETED" },
  { labelKey: "credentialScanning.scanStatusInProgress", value: "IN_PROGRESS" },
  { labelKey: "credentialScanning.scanStatusFailed",     value: "FAILED" },
  { labelKey: "credentialScanning.scanStatusPending",    value: "PENDING" },
];

const customizeColumnDict = {
  "Status": true,
  "Config": true,
  "Triggered": true,
  "Started": true,
  "Duration": true,
  "Findings": true,
  "Critical": true,
  "High": true,
  "Files": true,
  "Assets": true,
};

/**
 * Scan History table.
 * Props:
 *   configId   — when set, filters to a specific scan config
 *   embedded   — when true, skips breadcrumb and outer container
 */
function ScanHistory({ configId, embedded = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { height } = useWindowDimensions();
  const my_capabilities = GetCapabilities();
  const isCapable = my_capabilities[0];

  const filterOptions = useMemo(
    () => FILTER_KEYS.map((f) => ({ label: t(f.labelKey), value: f.value })),
    [t]
  );

  const [scans, setScans] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pending, setPending] = useState(true);
  const [slideChecked, setSlideChecked] = useState(false);
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("");

  const [checkedStateDict, setCheckedStateDict] = useState(customizeColumnDict);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [toBeDeleted, setToBeDeleted] = useState(null);
  const [colName, setColName] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewLogsScan, setViewLogsScan] = useState(null);
  const [openStopModal, setOpenStopModal] = useState(false);
  const [toBeStopped, setToBeStopped] = useState(null);
  const [stopColName, setStopColName] = useState(null);
  const [stoppingId, setStoppingId] = useState(null);

  const pollRef = useRef(null);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(localStorage.getItem("rowsPerPage_ScanHistory") || 10)
  );
  const tableHeightWithData = height - 220 + "px";
  const tableHeightEmpty = height - 220 + "px";
  const [datatableHeight, setDatatableHeight] = useState(tableHeightEmpty);

  useEffect(() => {
    GetColmn(setCheckedStateDict, pages_url, customizeColumnDict);
  }, []);

  const load = useCallback(async () => {
    setPending(true);
    const { data } = await getScanHistory({ configId });
    if (data) {
      const list = Array.isArray(data) ? data : [data];
      setScans(list);
      setFilteredData(list);
    }
    setPending(false);
    setSlideChecked(true);
  }, [configId]);

  useEffect(() => {
    if (!embedded) dispatch(changeTitle("Discovery"));
    load();
  }, [load, dispatch, embedded]);

  // Poll every 5s while any scan is active
  useEffect(() => {
    const hasActive = scans.some(
      (s) => s.status === "PENDING" || s.status === "IN_PROGRESS"
    );
    clearInterval(pollRef.current);
    if (hasActive) {
      pollRef.current = setInterval(load, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [scans, load]);

  // Filter / search
  useEffect(() => {
    let result = scans;
    if (filterBy) result = result.filter((s) => s.status === filterBy);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.config_name?.toLowerCase().includes(q) ||
          s.triggered_type?.toLowerCase().includes(q) ||
          s.status?.toLowerCase().includes(q)
      );
    }
    setFilteredData(result);
  }, [scans, filterBy, search]);

  useEffect(() => {
    const div = document.getElementById("credScanHistoryDivId");
    if (div && scans && scans.length > 0) {
      div.style.height = tableHeightWithData;
      setDatatableHeight(tableHeightWithData);
    } else {
      setDatatableHeight(tableHeightEmpty);
    }
  }, [scans]);

  useEffect(() => {
    document.title = t("credentialScanning.scanHistory");
    setSlideChecked(true);
  }, [t]);

  const handleSearch = useCallback((e) => { if (e) e.preventDefault(); }, []);

  const handleDeleteConfirm = (row) => {
    setToBeDeleted(row);
    setColName([t("credentialScanning.scanRowLabel", { id: row.id, status: row.status })]);
    setOpenDeleteModal(true);
  };

  const handleDeleteAction = async () => {
    if (!toBeDeleted) return;
    setDeletingId(toBeDeleted.id);
    const { error } = await deleteScan(toBeDeleted.id);
    setDeletingId(null);
    setOpenDeleteModal(false);
    setColName(null);
    setToBeDeleted(null);
    if (error) {
      showError(error?.error || t("credentialScanning.toastFailedToDeleteScan"));
    } else {
      showSuccess(t("credentialScanning.toastScanDeleted"));
      setScans((prev) => prev.filter((s) => s.id !== toBeDeleted?.id));
    }
  };

  const handleStopConfirm = (row) => {
    setToBeStopped(row);
    setStopColName([t("credentialScanning.scanRowLabel", { id: row.id, status: row.status })]);
    setOpenStopModal(true);
  };

  const handleStopAction = async () => {
    if (!toBeStopped) return;
    const target = toBeStopped;
    setStoppingId(target.id);
    const { error } = await stopScan(target.id);
    setStoppingId(null);
    setOpenStopModal(false);
    setStopColName(null);
    setToBeStopped(null);
    if (error) {
      showError(error?.error || t("credentialScanning.toastFailedToStopScan"));
    } else {
      showSuccess(t("credentialScanning.toastScanStopped"));
      // Optimistically reflect the state; the next poll will reconcile counts.
      setScans((prev) =>
        prev.map((s) =>
          s.id === target.id
            ? { ...s, status: "CANCELLED", completed_at: s.completed_at || new Date().toISOString() }
            : s
        )
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        name: t("credentialScanning.shColStatus"),
        selector: (row) => row.status,
        sortable: true,
        minWidth: "130px",
        omit: checkedStateDict["Status"] === false,
        cell: (row) => {
          const cfg = STATUS_CONFIG[row.status] || {};
          const { Icon } = cfg;
          return (
            <span
              className={`cred-scan-status-pill${cfg.pulse ? " pulse" : ""}`}
              style={{ background: cfg.bg, color: cfg.color }}
            >
              {Icon && <Icon size={13} />}
              {row.status?.replace("_", " ")}
            </span>
          );
        },
      },
      {
        name: t("credentialScanning.shColConfig"),
        selector: (row) => row.config_name,
        sortable: true,
        grow: 1,
        minWidth: "160px",
        omit: checkedStateDict["Config"] === false,
        cell: (row) => (
          <span className="fw-semibold small">{row.config_name || "—"}</span>
        ),
      },
      {
        name: t("credentialScanning.shColTriggered"),
        selector: (row) => row.triggered_type,
        minWidth: "110px",
        omit: checkedStateDict["Triggered"] === false,
        cell: (row) => (
          <span className="small" style={{ textTransform: "capitalize" }}>
            {row.triggered_type || "—"}
          </span>
        ),
      },
      {
        name: t("credentialScanning.shColStarted"),
        selector: (row) => row.started_at,
        sortable: true,
        minWidth: "160px",
        omit: checkedStateDict["Started"] === false,
        cell: (row) =>
          row.started_at ? (
            <span className="small">
              {new Date(row.started_at).toLocaleString()}
            </span>
          ) : (
            "—"
          ),
      },
      {
        name: t("credentialScanning.shColDuration"),
        width: "110px",
        omit: checkedStateDict["Duration"] === false,
        cell: (row) => (
          <span className="small">
            {row.duration_seconds != null
              ? _formatDuration(row.duration_seconds)
              : row.status === "IN_PROGRESS"
              ? t("credentialScanning.runningEllipsis")
              : "—"}
          </span>
        ),
      },
      {
        name: t("credentialScanning.shColFindings"),
        selector: (row) => row.total_findings,
        minWidth: "120px",
        omit: checkedStateDict["Findings"] === false,
        cell: (row) =>
          row.total_findings > 0 ? (
            <span
              className="cred-scan-count-badge cred-scan-count-badge--findings"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/dashboard/discovery/hardcoded-credentials/findings", {
                  state: { configId: row.config_id },
                });
              }}
              title={t("credentialScanning.titleViewFindings")}
            >
              {row.total_findings}
            </span>
          ) : (
            <span className="small text-muted">0</span>
          ),
      },
      {
        name: t("credentialScanning.shColCritical"),
        width: "95px",
        omit: checkedStateDict["Critical"] === false,
        cell: (row) =>
          row.critical_count > 0 ? (
            <span
              className="cred-scan-count-badge cred-scan-count-badge--critical"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/dashboard/discovery/hardcoded-credentials/findings", {
                  state: { configId: row.config_id, severity: "CRITICAL" },
                });
              }}
              title={t("credentialScanning.titleViewCriticalFindings")}
            >
              {row.critical_count}
            </span>
          ) : (
            <span className="small text-muted">0</span>
          ),
      },
      {
        name: t("credentialScanning.shColHigh"),
        width: "80px",
        omit: checkedStateDict["High"] === false,
        cell: (row) =>
          row.high_count > 0 ? (
            <span
              className="cred-scan-count-badge cred-scan-count-badge--high"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/dashboard/discovery/hardcoded-credentials/findings", {
                  state: { configId: row.config_id, severity: "HIGH" },
                });
              }}
              title={t("credentialScanning.titleViewHighFindings")}
            >
              {row.high_count}
            </span>
          ) : (
            <span className="small text-muted">0</span>
          ),
      },
      {
        name: t("credentialScanning.shColFiles"),
        selector: (row) => row.files_scanned,
        width: "85px",
        omit: checkedStateDict["Files"] === false,
        cell: (row) => (
          <span className="small">{row.files_scanned ?? 0}</span>
        ),
      },
      {
        name: t("credentialScanning.shColAssets"),
        selector: (row) => row.assets_scanned,
        width: "90px",
        omit: checkedStateDict["Assets"] === false,
        cell: (row) => (
          <span className="small">{row.assets_scanned ?? 0}</span>
        ),
      },
      {
        name: t("credentialScanning.colHdrAction"),
        width: "90px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        cell: (row) => {
          const canDelete =
            isCapable?.credential_scanning?.delete_scan &&
            ["IN_PROGRESS", "PENDING", "FAILED"].includes(row.status);
          const canStop =
            isCapable?.credential_scanning?.delete_scan &&
            ["PENDING", "IN_PROGRESS"].includes(row.status);

          return (
            <Nav>
              <NavDropdown
                id={`scan-history-action-${row.id}`}
                title={<BsThreeDotsVertical className="text-black" />}
                menuVariant="primary"
              >
                <NavDropdown.Item
                  onClick={() =>
                    navigate(
                      `/dashboard/discovery/hardcoded-credentials/scan-history/${row.id}/details`,
                      { state: { scan: row } }
                    )
                  }
                >
                  <MdOutlineInfo className="mx-2" size={13} />
                  {t("credentialScanning.menuDetails")}
                </NavDropdown.Item>
                <NavDropdown.Item onClick={() => setViewLogsScan(row)}>
                  <MdOutlineTerminal className="mx-2" size={13} />
                  {t("credentialScanning.menuViewLogs")}
                </NavDropdown.Item>
                {canStop && (
                  <NavDropdown.Item
                    onClick={() => handleStopConfirm(row)}
                    disabled={stoppingId === row.id}
                  >
                    <RiStopCircleLine className="mx-2" size={13} />
                    {t("credentialScanning.menuForceStop")}
                  </NavDropdown.Item>
                )}
                {canDelete && (
                  <NavDropdown.Item
                    onClick={() => handleDeleteConfirm(row)}
                    disabled={deletingId === row.id}
                  >
                    <RiDeleteBin5Line className="mx-2" size={13} />
                    {t("credentialScanning.menuForceDelete")}
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            </Nav>
          );
        },
      },
    ],
    [isCapable, deletingId, stoppingId, checkedStateDict, t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableContent = (
    <div
      id="credScanHistoryDivId"
      className="position-relative bg-white"
      style={{ height: embedded ? undefined : datatableHeight }}
    >
      <DataTable
        title={
          <>
            {!embedded && (
              <div className="py-2 pt-2">
                <BreadcrumbHeading
                  dataTableHeadingText={[
                    t("credentialScanning.breadcrumbBackTo"),
                    t("credentialScanning.scanHistory"),
                  ]}
                  dataTablePageLink={[
                    "/dashboard/discovery/hardcoded-credentials",
                    "/dashboard/discovery/hardcoded-credentials/scan-history",
                  ]}
                  pageNumber={4}
                />
              </div>
            )}
            <div className="mb-3">
              <ListHeading
                dataTableHeadingText={t("credentialScanning.scanHistory")}
                dataTableHeadingIcon={Images.NetworkAnalyzer}
              />
            </div>
          </>
        }
        columns={columns}
        data={filteredData}
        className="rdt_container"
        progressPending={pending}
        progressComponent={
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <div
              className="la-line-scale-pulse-out-rapid la-3x"
              style={{ color: "#5177ff" }}
            >
              <div className="rounded" />
              <div className="rounded" />
              <div className="rounded" />
              <div className="rounded" />
              <div className="rounded" />
            </div>
          </div>
        }
        noDataComponent={t("credentialScanning.noScanHistoryFound")}
        fixedHeader
        pagination
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[5, 10, 25, 50]}
        onChangeRowsPerPage={(n) => {
          localStorage.setItem("rowsPerPage_ScanHistory", n);
          setRowsPerPage(n);
        }}
        highlightOnHover
        pointerOnHover
        onRowClicked={(row) =>
          navigate(
            `/dashboard/discovery/hardcoded-credentials/scan-history/${row.id}/details`,
            { state: { scan: row } }
          )
        }
        subHeader
        subHeaderComponent={
          <SubHeaderComponent
            filterBy={filterBy}
            setFilterBy={setFilterBy}
            filterOptions={filterOptions}
            search={search}
            setSearch={setSearch}
            handleSearch={handleSearch}
            placeholder={t("credentialScanning.scanHistory")}
            pages_url={pages_url}
            checkedStateDict={checkedStateDict}
            setCheckedStateDict={setCheckedStateDict}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(n) => {
              localStorage.setItem("rowsPerPage_ScanHistory", n);
              setRowsPerPage(n);
            }}
            onRefresh={() => {
              setPending(true);
              load();
            }}
          />
        }
        subHeaderAlign="center"
        paginationIconFirstPage={
          <LazyLoadImage src={FirstPageIcon} alt="first page" />
        }
        paginationIconLastPage={
          <LazyLoadImage src={LastPageIcon} alt="last page" />
        }
        paginationIconNext={<LazyLoadImage src={NextPageIcon} alt="next" />}
        paginationIconPrevious={
          <LazyLoadImage src={PreviousPageIcon} alt="previous" />
        }
      />

      {colName && (
        <CustomModal
          open={openDeleteModal}
          handleClose={() => {
            setOpenDeleteModal(false);
            setColName(null);
            setToBeDeleted(null);
          }}
        >
          <ShowAlert
            setOpenAlert={setOpenDeleteModal}
            handleAlertAction={handleDeleteAction}
            colName={colName}
            alertMsg={t("credentialScanning.forceDeleteAlertMsg")}
            headingMsg={t("credentialScanning.forceDeleteHeading")}
          />
        </CustomModal>
      )}
      {openStopModal && (
        <CustomModal
          open={openStopModal}
          handleClose={() => {
            setOpenStopModal(false);
            setStopColName(null);
            setToBeStopped(null);
          }}
        >
          <ShowAlert
            setOpenAlert={setOpenStopModal}
            handleAlertAction={handleStopAction}
            colName={stopColName}
            alertMsg={t("credentialScanning.forceStopAlertMsg")}
            headingMsg={t("credentialScanning.forceStopHeading")}
          />
        </CustomModal>
      )}
    </div>
  );

  if (embedded) {
    return (
      <>
        {tableContent}
        {viewLogsScan && (
          <ScanLogsPanel scan={viewLogsScan} onClose={() => setViewLogsScan(null)} />
        )}
      </>
    );
  }

  return (
    <>
      {pending && scans.length === 0 ? (
        <DataTableSkeleton rowCount={7} columnCount={10} />
      ) : (
        <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div className="main_content_container p-3 w-100">
              {tableContent}
            </div>
          </Fade>
        </Slide>
      )}
      {viewLogsScan && (
        <ScanLogsPanel scan={viewLogsScan} onClose={() => setViewLogsScan(null)} />
      )}
    </>
  );
}

const _formatDuration = (seconds) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
};

export default ScanHistory;
